import React, { useState, useMemo, useEffect } from 'react';
import { Search, ShoppingCart, User, Grid, List, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { products, brands, navCategories } from '../mockData';
import axios from 'axios';
import Logo from '../components/Logo';

const LambrequinPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null); // Sera défini à la première collection chargée
  const [collections, setCollections] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [realProducts, setRealProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Charger les collections depuis l'API et définir la première comme sélectionnée
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/collections/`);
        const collectionsData = response.data || [];
        setCollections(collectionsData);
        
        // Définir la première collection comme sélectionnée par défaut
        if (collectionsData.length > 0 && !selectedCollection) {
          setSelectedCollection(collectionsData[0].name);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des collections:', error);
        setCollections([]);
      }
    };
    
    fetchCollections();
  }, []);

  // Fonction pour gérer la navigation des catégories
  const handleCategoryClick = (categoryName) => {
    const routes = {
      "Toile De Store": "/",
      "Toile de Store Droit": "/store-droit",
      "Toile de Store à Double Pente": "/store-double-pente",
      "Lambrequin Seul": "/lambrequin",
      "Toile De Pergola": "/pergola",
      "Brise-Vue De Balcon": "/brise-vue",
      "Toile de Store au Metre": "/toile-au-metre"
    };
    
    const route = routes[categoryName];
    if (route) {
      navigate(route);
    }
  };

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/suppliers/`);
        setSuppliers(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs:', error);
        setSuppliers([]);
      }
    };
    
    fetchSuppliers();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/products/`);
        
        const adaptedProducts = response.data.map(product => ({
          id: product.id,
          name: product.ref_frs || product.code1 || product.full_code || 'Produit',
          collection: product.gamme || 'N/A',
          brand: product.supplier || 'N/A',
          price: 0,
          image: product.image || null,
          slug: product.slug || product.id,
          thumbnail: product.thumbnail || product.image || null,
          supplier: product.supplier,
          category: product.category,
          code1: product.code1,
          ref_frs: product.ref_frs
        }));
        
        setRealProducts(adaptedProducts);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (realProducts.length === 0) return [];
    let filtered = [...realProducts];

    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection === selectedCollection);
    }

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    if (selectedColors.length > 0) {
      filtered = filtered.filter(p => selectedColors.includes(p.color));
    }

    if (selectedPatterns.length > 0) {
      filtered = filtered.filter(p => selectedPatterns.includes(p.pattern));
    }

    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch(sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }

    return filtered;
  }, [selectedCollection, selectedBrands, selectedColors, selectedPatterns, searchQuery, sortBy, realProducts]);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-8">
            <Logo showTagline={true} clickable={true} />

            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un produit, une référence..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="absolute right-0 top-0 bottom-0 bg-celadon text-white px-6 rounded-r-lg hover:bg-celadon-light transition-colors">
                  Chercher
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
                <User size={24} />
              </button>
              <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900 relative">
                <ShoppingCart size={24} />
                <span className="absolute -top-2 -right-2 bg-celadon text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">0</span>
                <span className="font-medium">PANIER</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-celadon text-white">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <ul className="flex items-center justify-center gap-2 mb-1">
            {navCategories.slice(0, 5).map((category, index) => (
              <li key={index}>
                <button 
                  onClick={() => handleCategoryClick(category.name)}
                  className="px-4 py-2 hover:bg-celadon-light transition-colors flex items-center gap-1 text-sm rounded"
                >
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
          <ul className="flex items-center justify-center gap-2">
            {navCategories.slice(5, 9).map((category, index) => (
              <li key={index + 5}>
                <button 
                  onClick={() => handleCategoryClick(category.name)}
                  className="px-4 py-2 hover:bg-celadon-light transition-colors flex items-center gap-1 text-sm rounded"
                >
                  {category.name}
                </button>
              </li>
            ))}
            <li>
              <button 
                onClick={() => navigate('/devis-marquage-flocage')}
                className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded"
              >
                Marquage/Flocage
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/devis-marquage-lumineux-flocage')}
                className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded"
              >
                Marquage Lumineux/Flocage
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/demande-devis')}
                className="px-4 py-2 bg-celadon hover:bg-celadon-light transition-colors text-sm font-medium rounded ml-2"
              >
                DEVIS
              </button>
            </li>
            <li>
              <button className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded">
                PRO
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer">Accueil</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900">Configurez votre lambrequin sur-mesure</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <Sidebar 
            selectedBrands={selectedBrands}
            setSelectedBrands={setSelectedBrands}
            selectedColors={selectedColors}
            setSelectedColors={setSelectedColors}
            selectedPatterns={selectedPatterns}
            setSelectedPatterns={setSelectedPatterns}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollection}
          />

          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Configurez votre lambrequin sur-mesure</h1>

            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <List size={20} />
                </button>
                {loading ? (
                  <span className="text-sm text-gray-600">Chargement des produits...</span>
                ) : (
                  <span className="text-sm text-gray-600">Il y a {filteredProducts.length} produits.</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Trier par:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-celadon"
                >
                  <option value="relevance">Pertinence</option>
                  <option value="name-asc">Nom, A à Z</option>
                  <option value="name-desc">Nom, Z à A</option>
                  <option value="price-asc">Prix, croissant</option>
                  <option value="price-desc">Prix, décroissant</option>
                </select>
              </div>
            </div>

            <ProductGrid products={filteredProducts} viewMode={viewMode} productType="lambrequin-seul" />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LambrequinPage;